import { formatCurrency } from '../utils/money'
import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import Navbar from '../components/Navbar'
import { rentalsApi, itemsApi } from '../lib/api'
import { assetUrl } from '../utils/assetUrl'

interface Rental {
  id: string
  status: string
  startDate: string
  endDate: string
  totalAmount: number
  isPaid: boolean
  createdAt: string
  item: {
    id: string
    title: string
    photos?: Array<{ url: string }>
    dailyPrice: number
  }
  owner: {
    id: string
    firstName: string
    lastName: string
    avatar?: string
  }
  renter?: {
    id: string
    firstName: string
    lastName: string
    avatar?: string
  }
  _count?: {
    messages: number
  }
}

const statusLabels: Record<string, string> = {
  PENDING: 'Pending approval',
  APPROVED: 'Approved',
  ACTIVE: 'Active',
  RETURNED: 'Return reported',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  DISPUTED: 'Disputed',
}

const statusClasses: Record<string, string> = {
  PENDING: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-blue-100 text-blue-800',
  ACTIVE: 'bg-green-100 text-green-800',
  RETURNED: 'bg-slate-100 text-slate-800',
  COMPLETED: 'bg-slate-100 text-slate-800',
  CANCELLED: 'bg-red-100 text-red-800',
  DISPUTED: 'bg-red-100 text-red-800',
}

type TabKey = 'renter' | 'owner' | 'history'

const HISTORY_PAGE_SIZE = 10

function PaginationBar({
  page,
  totalPages,
  onChange,
}: {
  page: number
  totalPages: number
  onChange: (page: number) => void
}) {
  if (totalPages <= 1) return null
  return (
    <div className="flex items-center justify-center gap-3 mt-4">
      <button
        type="button"
        onClick={() => onChange(page - 1)}
        disabled={page <= 1}
        className="btn btn-secondary btn-sm"
      >
        Previous
      </button>
      <span className="text-sm text-[var(--text-secondary)]">
        Page {page} of {totalPages}
      </span>
      <button
        type="button"
        onClick={() => onChange(page + 1)}
        disabled={page >= totalPages}
        className="btn btn-secondary btn-sm"
      >
        Next
      </button>
    </div>
  )
}

function RentalCard({
  rental,
  userId,
  showOwnerActions,
  actionLoadingId,
  onDecline,
  formatDate,
}: {
  rental: Rental
  userId?: string
  showOwnerActions: boolean
  actionLoadingId: string | null
  onDecline: (id: string) => void
  formatDate: (value: string) => string
}) {
  const navigate = useNavigate()
  return (
    <Link
      to={`/rental/${rental.id}`}
      className="group bg-white rounded-xl border border-[var(--border)] p-4 flex gap-4 overflow-hidden hover:border-[var(--forest-green)] hover:shadow-sm transition-all"
    >
      <div className="flex flex-col items-center gap-2 flex-shrink-0">
        <div className="w-24 h-24 bg-[var(--bg-tint)] rounded-lg flex-shrink-0 overflow-hidden flex items-center justify-center text-2xl">
          {rental.item.photos?.[0]?.url ? (
            <img
              src={assetUrl(rental.item.photos[0].url)}
              alt={rental.item.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <span>🔧</span>
          )}
        </div>
        {/* Under the thumbnail on narrow widths, where there's no room for it
            beside the title without squeezing the truncated title out entirely. */}
        <span
          className={`sm:hidden text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${
            statusClasses[rental.status] || 'bg-slate-100 text-slate-800'
          }`}
        >
          {statusLabels[rental.status] || rental.status}
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <span className="font-semibold text-[var(--text-primary)] truncate min-w-0 group-hover:underline">
            {rental.item.title}
          </span>
          <span
            className={`hidden sm:inline-flex text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap flex-shrink-0 ${
              statusClasses[rental.status] || 'bg-slate-100 text-slate-800'
            }`}
          >
            {statusLabels[rental.status] || rental.status}
          </span>
        </div>

        <p className="text-sm text-[var(--text-secondary)] mb-2">
          {rental.owner.id === userId && rental.renter
            ? `Renter: ${rental.renter.firstName} ${rental.renter.lastName}`
            : `Owner: ${rental.owner.firstName} ${rental.owner.lastName}`}
        </p>

        <div className="text-sm text-[var(--text-secondary)] space-y-1">
          <div>{formatDate(rental.startDate)} – {formatDate(rental.endDate)}</div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-medium text-[var(--text-primary)]">
              {formatCurrency(rental.totalAmount)}
            </span>
            {rental.isPaid ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                Paid
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
                Payment pending
              </span>
            )}
            {rental._count && rental._count.messages > 0 && (
              <span className="text-[var(--forest-green)]">
                {rental._count.messages} message{rental._count.messages !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        {showOwnerActions && rental.status === 'PENDING' && (
          <div className="flex gap-2 mt-3">
            {/* Deliberately navigates instead of approving inline -- a blind
                one-click Approve here silently fell back to whatever the
                renter originally requested (deliveryOption), with no way to
                pick Shipping/DoorDash instead. The real approval panel on
                RentalDetail.tsx is the only place fulfillment type is chosen. */}
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/rental/${rental.id}`) }}
              className="px-4 py-2 bg-[var(--forest-green)] hover:bg-[var(--emerald)] text-white rounded-lg font-medium text-sm transition-colors"
            >
              Review &amp; Approve
            </button>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDecline(rental.id) }}
              disabled={actionLoadingId === rental.id}
              className="px-4 py-2 bg-white border border-red-200 text-red-600 hover:bg-red-50 rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
            >
              Decline
            </button>
          </div>
        )}
      </div>
    </Link>
  )
}

export default function MyRentals() {
  const { user } = useAuth()
  const location = useLocation() as any
  const [tab, setTab] = useState<TabKey>(location.state?.tab === 'owner' ? 'owner' : 'renter')
  const [rentals, setRentals] = useState<Rental[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null)
  const [actionError, setActionError] = useState('')
  // There's no "OWNER" role in this app -- any user can own listings -- so whether to
  // show "Incoming Requests" is based on actually having listings, not a role field.
  const [hasListings, setHasListings] = useState(false)

  // History is two independently paginated lists (rented vs. loaned) shown as
  // their own sub-tabs (one visible at a time) rather than both stacked on the
  // page at once -- each keeps its own page/total/loading/error, and only the
  // active sub-tab's list is fetched.
  const [historySubTab, setHistorySubTab] = useState<'rented' | 'loaned'>('rented')

  const [rentedItems, setRentedItems] = useState<Rental[]>([])
  const [rentedPage, setRentedPage] = useState(1)
  const [rentedTotalPages, setRentedTotalPages] = useState(1)
  const [rentedTotal, setRentedTotal] = useState(0)
  const [rentedLoading, setRentedLoading] = useState(true)
  const [rentedError, setRentedError] = useState('')

  const [loanedItems, setLoanedItems] = useState<Rental[]>([])
  const [loanedPage, setLoanedPage] = useState(1)
  const [loanedTotalPages, setLoanedTotalPages] = useState(1)
  const [loanedTotal, setLoanedTotal] = useState(0)
  const [loanedLoading, setLoanedLoading] = useState(true)
  const [loanedError, setLoanedError] = useState('')

  useEffect(() => {
    if (tab === 'history') return
    fetchRentals(tab)
  }, [tab])

  useEffect(() => {
    if (tab !== 'history' || historySubTab !== 'rented') return
    fetchHistory('renter', rentedPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, historySubTab, rentedPage])

  useEffect(() => {
    if (tab !== 'history' || historySubTab !== 'loaned') return
    fetchHistory('owner', loanedPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, historySubTab, loanedPage])

  useEffect(() => {
    itemsApi.getMyItems({ page: 1, limit: 1 })
      .then((res) => setHasListings((res.data.meta?.total ?? 0) > 0))
      .catch(() => setHasListings(false))
  }, [])

  const fetchRentals = async (activeTab: TabKey) => {
    try {
      setIsLoading(true)
      setError('')
      const params = activeTab === 'owner'
        ? { role: 'owner', status: 'PENDING,APPROVED,ACTIVE,RETURNED,DISPUTED', page: 1, limit: 50 }
        : { role: 'renter', status: 'PENDING,APPROVED,ACTIVE,RETURNED,DISPUTED', page: 1, limit: 50 }
      const response = await rentalsApi.getAll(params)
      setRentals(response.data.data || [])
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not load your rentals.')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchHistory = async (role: 'renter' | 'owner', page: number) => {
    const setItems = role === 'renter' ? setRentedItems : setLoanedItems
    const setTotalPages = role === 'renter' ? setRentedTotalPages : setLoanedTotalPages
    const setTotal = role === 'renter' ? setRentedTotal : setLoanedTotal
    const setLoading = role === 'renter' ? setRentedLoading : setLoanedLoading
    const setListError = role === 'renter' ? setRentedError : setLoanedError
    try {
      setLoading(true)
      setListError('')
      const response = await rentalsApi.getAll({ role, status: 'COMPLETED,CANCELLED', page, limit: HISTORY_PAGE_SIZE })
      setItems(response.data.data || [])
      setTotalPages(response.data.meta?.totalPages || 1)
      setTotal(response.data.meta?.total || 0)
    } catch (err: any) {
      setListError(err?.response?.data?.error || 'Could not load your rental history.')
    } finally {
      setLoading(false)
    }
  }

  const handleDecline = async (rentalId: string) => {
    setActionLoadingId(rentalId)
    setActionError('')
    try {
      await rentalsApi.decline(rentalId, '')
      setRentals((prev) => prev.filter((r) => r.id !== rentalId))
    } catch (err: any) {
      setActionError(err?.response?.data?.error || 'Failed to decline rental.')
    } finally {
      setActionLoadingId(null)
    }
  }

  const formatDate = (value: string) => {
    return new Date(value).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
      <Navbar />

      <main className="min-h-[calc(100vh-var(--navbar-height))]">
        <div className="container mx-auto px-4 py-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-[var(--text-primary)]">My Rentals</h1>
            <p className="text-[var(--text-secondary)] mt-1">
              {tab === 'renter'
                ? `Items you are renting${user?.firstName ? `, ${user.firstName}` : ''}`
                : tab === 'owner'
                ? 'Rentals of your listed items, including requests awaiting approval'
                : 'Completed and cancelled rentals, as renter or owner'}
            </p>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            <button
              type="button"
              onClick={() => setTab('renter')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                tab === 'renter'
                  ? 'bg-[var(--forest-green)] text-white'
                  : 'bg-white border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-tint)]'
              }`}
            >
              Renting
            </button>
            {hasListings && (
              <button
                type="button"
                onClick={() => setTab('owner')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  tab === 'owner'
                    ? 'bg-[var(--forest-green)] text-white'
                    : 'bg-white border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-tint)]'
                }`}
              >
                My Listings
              </button>
            )}
            <button
              type="button"
              onClick={() => setTab('history')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                tab === 'history'
                  ? 'bg-[var(--forest-green)] text-white'
                  : 'bg-white border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-tint)]'
              }`}
            >
              History
            </button>
          </div>

          {actionError && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 mb-4 text-sm">
              {actionError}
            </div>
          )}

          {tab !== 'history' && (
            <>
              {isLoading && (
                <div className="text-center py-12 text-[var(--text-muted)]">Loading...</div>
              )}

              {!isLoading && error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 mb-6">
                  {error}
                </div>
              )}

              {!isLoading && !error && rentals.length === 0 && (
                <div className="bg-white rounded-xl border border-[var(--border)] p-8 text-center">
                  <div className="text-4xl mb-3">🔧</div>
                  <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
                    {tab === 'owner' ? 'No rentals yet' : 'No rentals yet'}
                  </h2>
                  <p className="text-[var(--text-secondary)] mb-4">
                    {tab === 'owner'
                      ? 'No one has rented your items yet.'
                      : "You haven't rented any items. Browse listings to find what you need."}
                  </p>
                  {tab === 'renter' && (
                    <Link
                      to="/browse"
                      className="inline-block px-6 py-2.5 bg-[var(--forest-green)] text-white rounded-lg font-medium"
                    >
                      Browse Items
                    </Link>
                  )}
                </div>
              )}

              {!isLoading && rentals.length > 0 && (
                <div className="grid gap-4">
                  {rentals.map((rental) => (
                    <RentalCard
                      key={rental.id}
                      rental={rental}
                      userId={user?.id}
                      showOwnerActions={tab === 'owner'}
                      actionLoadingId={actionLoadingId}
                      onDecline={handleDecline}
                      formatDate={formatDate}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {tab === 'history' && (() => {
            const active = historySubTab === 'rented'
              ? { items: rentedItems, page: rentedPage, totalPages: rentedTotalPages, total: rentedTotal, loading: rentedLoading, error: rentedError, setPage: setRentedPage }
              : { items: loanedItems, page: loanedPage, totalPages: loanedTotalPages, total: loanedTotal, loading: loanedLoading, error: loanedError, setPage: setLoanedPage }

            return (
              <>
                <div className="flex gap-2 mb-6">
                  <button
                    type="button"
                    onClick={() => setHistorySubTab('rented')}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                      historySubTab === 'rented'
                        ? 'bg-[var(--forest-green)] text-white'
                        : 'bg-white border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-tint)]'
                    }`}
                  >
                    Items you rented
                  </button>
                  <button
                    type="button"
                    onClick={() => setHistorySubTab('loaned')}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                      historySubTab === 'loaned'
                        ? 'bg-[var(--forest-green)] text-white'
                        : 'bg-white border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-tint)]'
                    }`}
                  >
                    Items you loaned
                  </button>
                </div>

                {active.loading && (
                  <div className="text-center py-12 text-[var(--text-muted)]">Loading...</div>
                )}

                {!active.loading && active.error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4">
                    {active.error}
                  </div>
                )}

                {!active.loading && !active.error && active.total === 0 && (
                  <div className="bg-white rounded-xl border border-[var(--border)] p-8 text-center">
                    <div className="text-4xl mb-3">🔧</div>
                    <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-2">No past rentals yet</h2>
                    <p className="text-[var(--text-secondary)]">
                      {historySubTab === 'rented'
                        ? 'Rentals you complete or cancel as a renter will show up here.'
                        : 'Rentals of your items that complete or get cancelled will show up here.'}
                    </p>
                  </div>
                )}

                {!active.loading && !active.error && active.total > 0 && (
                  <>
                    <div className="grid gap-4">
                      {active.items.map((rental) => (
                        <RentalCard
                          key={rental.id}
                          rental={rental}
                          userId={user?.id}
                          showOwnerActions={false}
                          actionLoadingId={actionLoadingId}
                          onDecline={handleDecline}
                          formatDate={formatDate}
                        />
                      ))}
                    </div>
                    <PaginationBar page={active.page} totalPages={active.totalPages} onChange={active.setPage} />
                  </>
                )}
              </>
            )
          })()}
        </div>
      </main>
    </div>
  )
}